// Root app — SPA mode: single screen at a time, sidebar navigates.
//
// Cross-file symbols (screens, primitives, useT, …) are injected by the
// pp-inject-globals Vite plugin at transform time; no explicit imports
// needed here.

import { useAuth } from './src/lib/auth.jsx';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "lang": "ko",
  "theme": "paper",
  "viewport": "auto"
}/*EDITMODE-END*/;

// Device-simulator presets — each is the visible window we pretend the
// browser has. The existing fit() logic in index.html letterbox-scales the
// 1480×900 design canvas into whichever box we choose.
const VIEWPORTS = {
  auto:    { w: null, h: null, label: '자동', labelEn: 'Auto',    icon: '🖥' },
  mobile:  { w: 390,  h: 844,  label: '모바일', labelEn: 'Mobile', icon: '📱' },
  tablet:  { w: 1024, h: 768,  label: '태블릿', labelEn: 'Tablet', icon: '⊟' },
  pc:      { w: 1480, h: 900,  label: 'PC',    labelEn: 'PC',     icon: '🖥' },
};

// Map nav id → React component
const SCREENS = {
  dashboard: DashboardScreen,
  explore:   ExploreScreen,
  detail:    DetailScreen,    // not in sidebar by default; reached via Explore/⌘K
  reader:    ReaderScreen,    // not in sidebar; reached from detail/explore
  bench:     BenchScreen,
  projects:  ProjectBoardScreen,
  graph:     GraphScreen,
  wiki:      WikiScreen,
  questions: QuestionsScreen,
  assistant: AssistantScreen,
  library:   ExploreScreen,   // "Library" = filtered explore for now
  reports:   DashboardScreen, // placeholder route
  settings:  SettingsScreen,
  integrations: SettingsScreen, // routes to integrations section
};

function PaperPilotApp() {
  const [tw, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const auth = useAuth();
  const settingsLoadedRef = React.useRef(false);

  // Apply theme whenever it changes
  React.useEffect(() => { applyTheme(tw.theme); }, [tw.theme]);

  // Pull user-saved language/theme on sign-in and merge into tweaks.
  React.useEffect(() => {
    if (!auth.user) { settingsLoadedRef.current = false; return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/settings', { credentials: 'same-origin' });
        const body = await res.json();
        if (cancelled || !body?.ok) return;
        const s = body.data ?? {};
        const edits = {};
        if (s.language && s.language !== tw.lang)   edits.lang = s.language;
        if (s.theme    && s.theme    !== tw.theme)  edits.theme = s.theme;
        if (Object.keys(edits).length) setTweak(edits);
      } catch {}
      finally { settingsLoadedRef.current = true; }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id]);

  // Debounced save when language/theme change while signed in.
  React.useEffect(() => {
    if (!auth.user || !settingsLoadedRef.current) return;
    const t = setTimeout(() => {
      fetch('/api/settings', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ language: tw.lang, theme: tw.theme }),
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [auth.user?.id, tw.lang, tw.theme]);

  // Push viewport-simulator selection to the fit() routine in index.html
  React.useEffect(() => {
    const v = VIEWPORTS[tw.viewport] || VIEWPORTS.auto;
    window.__ppViewportSim = v.w ? { w: v.w, h: v.h, key: tw.viewport } : null;
    if (typeof window.__ppFit === 'function') window.__ppFit();
  }, [tw.viewport]);

  // Active route + params — persisted to URL hash so reload keeps your place.
  // Hash format: `#/screen` or `#/screen/<id>` (e.g. `#/detail/p3`,
  // `#/reader/2605.18334`). `params.id` is what each screen reads.
  function parseHash() {
    const raw = (window.location.hash || '').replace(/^#\/?/, '').trim();
    if (!raw) return { screen: 'dashboard', params: {} };
    const [screen, ...rest] = raw.split('/');
    const id = rest.length ? decodeURIComponent(rest.join('/')) : null;
    if (!SCREENS[screen]) return { screen: 'dashboard', params: {} };
    return { screen, params: id ? { id } : {} };
  }

  const [routeState, setRouteState] = React.useState(parseHash);
  const route = routeState.screen;

  // Browser back/forward
  React.useEffect(() => {
    const onPop = () => setRouteState(parseHash());
    window.addEventListener('hashchange', onPop);
    return () => window.removeEventListener('hashchange', onPop);
  }, []);

  // navigate(screen) or navigate(screen, { id }) — updates state + hash.
  const navigate = React.useCallback((id, params = {}) => {
    if (!SCREENS[id]) return;
    const next = { screen: id, params };
    setRouteState(next);
    const target = params?.id ? `#/${id}/${encodeURIComponent(params.id)}` : `#/${id}`;
    if (window.location.hash !== target) {
      history.pushState(null, '', target);
    }
    // scroll main pane to top
    setTimeout(() => {
      document.querySelectorAll('.pp-main, .pp-content > *').forEach(el => { try { el.scrollTop = 0; } catch {} });
    }, 0);
  }, []);

  // Keyboard shortcuts — G followed by a letter for quick nav (cmd palette style)
  React.useEffect(() => {
    let pendingG = false;
    let gTimer = null;
    const map = { d: 'dashboard', e: 'explore', l: 'library', b: 'bench', p: 'projects', g: 'graph', w: 'wiki', q: 'questions', r: 'reports', a: 'assistant' };
    const onKey = (e) => {
      // Ignore when typing in inputs
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (pendingG) {
        pendingG = false; clearTimeout(gTimer);
        if (map[e.key.toLowerCase()]) {
          e.preventDefault();
          navigate(map[e.key.toLowerCase()]);
        }
        return;
      }
      if (e.key === 'g') {
        pendingG = true;
        gTimer = setTimeout(() => { pendingG = false; }, 800);
        return;
      }
      // ⌘J → assistant
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); navigate('assistant');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const Screen = SCREENS[route] || DashboardScreen;

  return (
    <LangProvider lang={tw.lang}>
      <NavContext.Provider value={{ navigate, route, params: routeState.params }}>
        <div className="pp pp-app" data-route={route}>
          <Screen route={route} params={routeState.params}/>
        </div>

        {/* Global ⌘K command palette */}
        <CommandPalette/>

        <TweaksPanel title={tw.lang === 'ko' ? 'Tweaks · 환경설정' : 'Tweaks · Preferences'} noDeckControls>
          <TweakSection label={tw.lang === 'ko' ? '언어' : 'Language'} />
          <TweakRadio
            label={tw.lang === 'ko' ? '표기' : 'Display'}
            value={tw.lang}
            options={[
              { value: 'ko', label: '한국어' },
              { value: 'en', label: 'English' },
            ]}
            onChange={(v) => setTweak('lang', v)}
          />

          <TweakSection label={tw.lang === 'ko' ? '테마' : 'Theme'} />
          <TweakRadio
            label={tw.lang === 'ko' ? '프리셋' : 'Preset'}
            value={tw.theme}
            options={Object.entries(THEMES).map(([k, v]) => ({
              value: k,
              label: tw.lang === 'ko' ? v.name : v.nameEn,
            }))}
            onChange={(v) => setTweak('theme', v)}
          />

          <div className="pp-tweaks-swatches">
            {Object.entries(THEMES).map(([k, theme]) => (
              <button key={k}
                className={'pp-tweaks-swatch' + (tw.theme === k ? ' is-on' : '')}
                onClick={() => setTweak('theme', k)}
                title={tw.lang === 'ko' ? theme.description : theme.description}
              >
                <div className="pp-tweaks-swatch-strip">
                  {theme.swatch.map((c, i) => (
                    <div key={i} style={{ background: c, flex: i === 0 ? 2 : 1 }}/>
                  ))}
                </div>
                <div className="pp-tweaks-swatch-name">{tw.lang === 'ko' ? theme.name : theme.nameEn}</div>
              </button>
            ))}
          </div>

          <TweakSection label={tw.lang === 'ko' ? '뷰포트 시뮬레이터' : 'Viewport simulator'} />
          <div className="pp-tweaks-devices">
            {Object.entries(VIEWPORTS).map(([k, v]) => (
              <button key={k}
                className={'pp-tweaks-device' + (tw.viewport === k ? ' is-on' : '')}
                onClick={() => setTweak('viewport', k)}
                title={v.w ? `${v.w} × ${v.h}` : (tw.lang === 'ko' ? '실제 화면 크기' : 'Native window')}
              >
                <div className="pp-tweaks-device-icon">{v.icon}</div>
                <div className="pp-tweaks-device-name">{tw.lang === 'ko' ? v.label : v.labelEn}</div>
                <div className="pp-tweaks-device-dim">
                  {v.w ? `${v.w}×${v.h}` : (tw.lang === 'ko' ? '실제 크기' : 'native')}
                </div>
              </button>
            ))}
          </div>
        </TweaksPanel>
      </NavContext.Provider>
    </LangProvider>
  );
}

// Inject swatch + app shell styles
if (typeof document !== 'undefined' && !document.getElementById('pp-tweaks-extra')) {
  const s = document.createElement('style');
  s.id = 'pp-tweaks-extra';
  s.textContent = `
  .pp-app {
    width: 100vw; height: 100vh;
    min-width: 1280px;
    overflow: hidden;
    background: var(--bg-0);
  }
  .pp-app .pp-screen { height: 100%; }
  /* Outer body becomes scrollable below the min-width so the design stays intact */
  html, body { min-width: 1280px; }
  body { overflow-x: auto; overflow-y: hidden; }

  .pp-tweaks-swatches {
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
    padding: 6px 12px 12px;
  }
  .pp-tweaks-swatch {
    display: flex; flex-direction: column; gap: 5px;
    padding: 6px;
    border: 1px solid rgba(0,0,0,0.10);
    background: #fff;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    font: inherit;
    transition: border-color .12s, box-shadow .12s;
  }
  .pp-tweaks-swatch:hover { border-color: rgba(0,0,0,0.30); }
  .pp-tweaks-swatch.is-on {
    border-color: #c96442;
    box-shadow: 0 0 0 2px rgba(201,100,66,0.20);
  }
  .pp-tweaks-swatch-strip {
    display: flex; height: 22px; border-radius: 3px; overflow: hidden;
    box-shadow: inset 0 0 0 0.5px rgba(0,0,0,0.10);
  }
  .pp-tweaks-swatch-name {
    font-size: 11px; color: #29261b; font-weight: 500;
  }

  /* Device-simulator chooser */
  .pp-tweaks-devices {
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
    padding: 6px 12px 12px;
  }
  .pp-tweaks-device {
    display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
    padding: 8px 10px;
    border: 1px solid rgba(0,0,0,0.10);
    background: #fff;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: #29261b;
    transition: border-color .12s, box-shadow .12s;
  }
  .pp-tweaks-device:hover { border-color: rgba(0,0,0,0.30); }
  .pp-tweaks-device.is-on {
    border-color: #c96442;
    box-shadow: 0 0 0 2px rgba(201,100,66,0.20);
  }
  .pp-tweaks-device-icon { font-size: 14px; line-height: 1; }
  .pp-tweaks-device-name { font-size: 11.5px; font-weight: 600; }
  .pp-tweaks-device-dim {
    font-size: 10px; color: rgba(41,38,27,0.55);
    font-variant-numeric: tabular-nums; letter-spacing: 0.02em;
  }
  `;
  document.head.appendChild(s);
}

export { PaperPilotApp, TWEAK_DEFAULTS, SCREENS, VIEWPORTS };
